import { DashboardLayout } from "components/DashboardLayout";
import Async from "components/DesignSystem/AsyncComponents/Async";
import { Button } from "components/DesignSystem/Button/Button";
import Card from "components/DesignSystem/Card/Card";
import { PaginationHandler } from "components/DesignSystem/Pagination/Pagination";
import { Search } from "components/DesignSystem/Search/Search";
import { Skeleton } from "components/DesignSystem/Skeleton/Skeleton";
import Table from "components/DesignSystem/Table/V2/Table";
import {
  ToggleGroup,
  ToggleGroupItem,
} from "components/DesignSystem/ToggleGroup/ToggleGroup";
import { EmptyList } from "components/Illustrations/EmptyList";
import { TableEmptyState } from "components/TableEmptyState/TableEmptyState";
import { useFilters } from "hooks/useFilter";
import { useQuery, useUpdateQuery } from "hooks/useQuery";
import { useState } from "react";
import { flexRender, getCoreRowModel, useReactTable } from "react-table-8.10.7";
import { useGetBroadcastListQuery } from "store/apis/broadcastCall";
import { BroadcastFilterOptions } from "types/Models/broadcast";
import {
  BroadcastCallFilterAll,
  BroadcastCallFilterUpcoming,
} from "types/Models/broadcastCall";
import { debounce } from "utils/debouncing";
import {
  BroadcastTitleColumn,
  createBroadcastTypesColumn,
  createBroadcastStatusColumn,
  createIVRStatusColumn,
  BroadcastScheduleColumn,
  BroadcastActionsColumn,
} from "./BroadcastTrackerColumns";

const initialValue: BroadcastFilterOptions = {
  BROADCAST_TYPES: null,
  STATUS_FILTER: null,
  START_DATE: "",
  END_DATE: "",
  SELECT_PERIOD: "",
  IVR_STATUS: null,
};

export const BroadcastTracker = () => {
  const [broadcastType, setBroadcastType] = useState<
    typeof BroadcastCallFilterUpcoming | typeof BroadcastCallFilterAll
  >(BroadcastCallFilterUpcoming);
  const query = useQuery();
  const { update: updateQuery } = useUpdateQuery();
  const page = query.get("page_num") || "1";
  const searchTerm = query.get("query") || "";
  const { values, updateFilter } = useFilters<BroadcastFilterOptions>({
    initialValue: initialValue,
    useQueryParams: true,
  });

  const isAnyFilterActive = Object.values(values).some((value) =>
    Array.isArray(value) ? value.length > 0 : Boolean(value)
  );

  const { data, isLoading, isSuccess } = useGetBroadcastListQuery({
    page: Number(page),
    filter: broadcastType,
    broadcast_types: values.BROADCAST_TYPES || null,
    status: values.STATUS_FILTER || null,
    scheduled_date_from: values.START_DATE || null,
    scheduled_date_to: values.END_DATE || null,
    search_term: searchTerm || null,
    ivr_status: values.IVR_STATUS || null,
  });

  const resetFilter = () => {
    Object.keys(initialValue).forEach((key) => {
      updateFilter(
        key as keyof BroadcastFilterOptions,
        initialValue[key as keyof BroadcastFilterOptions]
      );
    });
  };

  const columns = [
    BroadcastTitleColumn,
    createBroadcastTypesColumn(values),
    createBroadcastStatusColumn(values),
    createIVRStatusColumn(values),
    BroadcastScheduleColumn,
    BroadcastActionsColumn,
  ];

  const handleChange = debounce((e) => {
    updateQuery({
      query: "query",
      value: e.target.value,
    });
  });

  const table = useReactTable({
    data: data?.broadcasts || [],
    columns: columns,
    getCoreRowModel: getCoreRowModel(),
    defaultColumn: {
      minSize: 5,
    },
  });

  return (
    <DashboardLayout
      className="t-mt-5 t-flex-1 t-h-[calc(100%-20px)]"
      header={
        <div className="t-flex t-flex-col t-gap-2.5">
          <div>
            <ToggleGroup value={broadcastType}>
              <ToggleGroupItem
                value={BroadcastCallFilterUpcoming}
                onClick={() => setBroadcastType(BroadcastCallFilterUpcoming)}
              >
                Upcoming
              </ToggleGroupItem>
              <ToggleGroupItem
                value={BroadcastCallFilterAll}
                onClick={() => setBroadcastType(BroadcastCallFilterAll)}
              >
                All
              </ToggleGroupItem>
            </ToggleGroup>
          </div>
          <div className="t-flex t-items-center t-justify-between">
            <div className="t-w-1/2">
              <Search block onChange={handleChange} defaultValue={searchTerm} />
            </div>
            <div className="t-flex t-gap-3">
              {isAnyFilterActive && (
                <Button size="xsmall" onClick={resetFilter}>
                  Clear all filters
                </Button>
              )}
              {data && (
                <PaginationHandler
                  page_num={data.pagination.page}
                  per_page={data.pagination.page_size}
                  total_count={data.pagination.total_count}
                  total_pages={data.pagination.total_pages}
                  onPageNumChange={(page) =>
                    updateQuery({
                      query: "page_num",
                      value: page,
                    })
                  }
                />
              )}
            </div>
          </div>
        </div>
      }
    >
      <div>
        {isLoading ? (
          <Card.Body className="t-space-y-3">
            <Skeleton className="t-min-h-10" />
            <Skeleton className="t-min-h-10" />
            <Skeleton className="t-min-h-10" />
            <Skeleton className="t-min-h-10" />
            <Skeleton className="t-min-h-10" />
            <Skeleton className="t-min-h-10" />
          </Card.Body>
        ) : (
          <Table.Container sticky className="t-h-full">
            <Table.Content>
              <Table.Head>
                {table.getHeaderGroups().map((headerGroup) => (
                  <Table.Row key={headerGroup.id} className="t-z-[11]">
                    {headerGroup?.headers?.map((header) => (
                      <Table.HeadCell
                        key={header.id}
                        style={{
                          width: `${header.getSize()}%`,
                        }}
                        showDivider={Boolean(
                          //@ts-ignore
                          header.column.columnDef.meta?.showDivider
                        )}
                      >
                        {header.isPlaceholder
                          ? null
                          : flexRender(
                              header.column.columnDef.header,
                              header.getContext()
                            )}
                      </Table.HeadCell>
                    ))}
                  </Table.Row>
                ))}
              </Table.Head>
              <Async.Root
                isEmpty={data?.pagination.total_count === 0}
                isLoading={isLoading}
                isSuccess={isSuccess}
              >
                <Async.Empty>
                  <div className="t-relative">
                    <div className="t-absolute t-h-96 t-w-[1000px] t-flex t-items-center t-justify-center">
                      <TableEmptyState
                        icon={<EmptyList />}
                        title="No records found"
                      />
                    </div>
                  </div>
                </Async.Empty>
                <Async.Success>
                  <Table.Body>
                    {table.getRowModel().rows.map((row) => (
                      <Table.Row
                        key={row.id}
                        className="t-group hover:t-bg-surface-lighter-grey t-cursor-pointer"
                      >
                        {row.getVisibleCells().map((cell) => (
                          <Table.Cell
                            key={cell.id}
                            style={{
                              width: `${cell.column.getSize()}%`,
                            }}
                          >
                            {flexRender(
                              cell.column.columnDef.cell,
                              cell.getContext()
                            )}
                          </Table.Cell>
                        ))}
                      </Table.Row>
                    ))}
                  </Table.Body>
                </Async.Success>
              </Async.Root>
            </Table.Content>
          </Table.Container>
        )}
      </div>
    </DashboardLayout>
  );
};
